/**
 * QualityScore Component
 *
 * Displays a quality score (1-5 scale) with optional stars
 */

import React from 'react';
import { Star as StarIcon, StarBorder as StarBorderIcon } from '@mui/icons-material';
import { Box, Tooltip,Typography } from '@mui/material';

import { QUALITY_COLORS, QUALITY_LABELS } from '../../constants';

interface QualityScoreProps {
  /** The quality score (1-5) */
  score: number | null | undefined;
  /** Display variant */
  variant?: 'stars' | 'numeric' | 'badge';
  /** Size variant */
  size?: 'small' | 'medium';
  /** Show tooltip with label */
  showTooltip?: boolean;
}

export const QualityScore: React.FC<QualityScoreProps> = ({
  score,
  variant = 'numeric',
  size = 'small',
  showTooltip = true,
}) => {
  if (score === null || score === undefined) {
    return (
      <Typography
        variant="body2"
        sx={{
          color: 'text.disabled',
          fontSize: size === 'small' ? '0.75rem' : '0.875rem',
        }}
      >
        —
      </Typography>
    );
  }

  // Clamp score to 1-5 range
  const clampedScore = Math.max(1, Math.min(5, Math.round(score)));
  const color = QUALITY_COLORS[clampedScore];
  const label = QUALITY_LABELS[clampedScore];

  const renderStars = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <Box
          key={i}
          sx={{
            color: i <= clampedScore ? color : 'grey.300',
            fontSize: size === 'small' ? '0.875rem' : '1rem',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {i <= clampedScore ? (
            <StarIcon fontSize="inherit" />
          ) : (
            <StarBorderIcon fontSize="inherit" />
          )}
        </Box>
      ))}
    </Box>
  );

  const renderNumeric = () => (
    <Typography
      variant="body2"
      sx={{
        color,
        fontWeight: 600,
        fontSize: size === 'small' ? '0.875rem' : '1rem',
      }}
    >
      {clampedScore}/5
    </Typography>
  );

  const renderBadge = () => (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        bgcolor: `${color}15`,
        color,
        borderRadius: 1,
        padding: size === 'small' ? '2px 8px' : '4px 10px',
        fontSize: size === 'small' ? '0.75rem' : '0.875rem',
        fontWeight: 600,
      }}
    >
      <StarIcon sx={{ fontSize: size === 'small' ? '0.875rem' : '1rem' }} />
      {clampedScore}
    </Box>
  );

  const content = (() => {
    switch (variant) {
      case 'stars':
        return renderStars();
      case 'badge':
        return renderBadge();
      case 'numeric':
      default:
        return renderNumeric();
    }
  })();

  if (showTooltip) {
    return <Tooltip title={label}>{content}</Tooltip>;
  }

  return content;
};

export default QualityScore;

